/**
 * Transport layer for the Tahiti API.
 *
 * In dev, requests go to a relative `/api/...` path which Vite proxies to
 * Tahiti (prod by default, devdb as a dev-panel fallback — see
 * vite.config.ts) — this keeps the session cookie first-party. Every call
 * sends `credentials:'include'` and, when present, a Bearer JWT from
 * localStorage. Every response is wrapped in the Tahiti envelope, whose
 * `success` flag can be `false` even on HTTP 200 — both cases throw.
 */
import { tahitiEnvelopeSchema, tahitiErrorSchema } from '@/schemas/tahiti';
import type { z } from 'zod';

const TOKEN_KEY = 'tahiti_token';

/** Transport / API error. Mirrors the shape of `api.ts`'s ApiError. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── Auth token ──────────────────────────────────────────────────────────────

function safeLocalStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/** Read the stored JWT, or null when absent / localStorage unavailable. */
export function getToken(): string | null {
  return safeLocalStorage()?.getItem(TOKEN_KEY) ?? null;
}

/** Persist (or clear, when passed null) the JWT. */
export function setToken(token: string | null): void {
  const storage = safeLocalStorage();
  if (!storage) return;
  if (token === null) storage.removeItem(TOKEN_KEY);
  else storage.setItem(TOKEN_KEY, token);
}

/**
 * The `workContextId` claim decoded from the stored JWT, or null when there is
 * no token / claim. Tahiti writes resolve the work context (workspace + role)
 * from the JWT itself, so the `X-Work-Context-Id` header is belt-and-suspenders
 * — sent when available, never required (verified: a claim-less token still
 * writes fine).
 */
export function workContextId(): string | null {
  const token = getToken();
  const payload = token?.split('.')[1];
  if (!payload) return null;
  try {
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.padEnd(Math.ceil(b64.length / 4) * 4, '=');
    const claims = JSON.parse(atob(padded)) as { workContextId?: unknown };
    return claims.workContextId == null ? null : String(claims.workContextId);
  } catch {
    return null;
  }
}

// ── Unauthorized handler ──────────────────────────────────────────────────────

let unauthorizedHandler: (() => void) | null = null;

/** Register (or clear) a callback fired whenever a request returns HTTP 401. */
export function registerUnauthorizedHandler(fn: (() => void) | null): void {
  unauthorizedHandler = fn;
}

/**
 * Fire the registered unauthorized handler. For callers that issue their own
 * fetches (e.g. the bulk draft API) rather than going through {@link tahitiRequest}
 * but still want a 401 to trigger the same session-expiry redirect.
 */
export function notifyUnauthorized(): void {
  unauthorizedHandler?.();
}

// ── Request helper ────────────────────────────────────────────────────────────

function errorCode(raw: unknown, status: number): { code: string; message: string } {
  const parsed = tahitiErrorSchema.safeParse(raw);
  if (parsed.success) {
    const code = parsed.data.error?.code;
    return {
      code: code === undefined ? `HTTP_${status}` : String(code),
      message: parsed.data.message ?? `Request failed with status ${status}`,
    };
  }
  return { code: `HTTP_${status}`, message: `Request failed with status ${status}` };
}

/**
 * Issue a Tahiti request and return the unwrapped `data`, validated against
 * `dataSchema`. Throws {@link ApiError} on HTTP error or `success:false`.
 * Fires the registered unauthorized handler on HTTP 401.
 */
export async function tahitiRequest<T>(
  path: string,
  dataSchema: z.ZodType<T>,
  init?: RequestInit,
): Promise<T> {
  const token = getToken();
  const wcid = workContextId();
  const response = await fetch(`/api${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(wcid ? { 'X-Work-Context-Id': wcid } : {}),
      ...init?.headers,
    },
  });

  const raw: unknown = await response.json().catch(() => ({}));

  if (response.status === 401) {
    unauthorizedHandler?.();
    const { code, message } = errorCode(raw, 401);
    throw new ApiError(401, code, message);
  }

  if (!response.ok) {
    const { code, message } = errorCode(raw, response.status);
    throw new ApiError(response.status, code, message);
  }

  // A logical failure (success:false) can ride an HTTP 200 and its body omits a
  // schema-valid `data`; detect it before strictly validating the envelope,
  // otherwise the data-schema parse throws a ZodError instead of an ApiError.
  if (raw !== null && typeof raw === 'object' && (raw as { success?: unknown }).success === false) {
    const { code, message } = errorCode(raw, response.status);
    throw new ApiError(response.status, code, message);
  }

  // `z.ZodType<T>` widens the inferred `data` to `T | undefined`; re-narrow via
  // a typed local since the schema guarantees `data` is present and `T`-shaped.
  const envelope = tahitiEnvelopeSchema(dataSchema).parse(raw) as {
    success: boolean;
    data: T;
  };
  return envelope.data;
}
